import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  INVOICE_STATUS_MAP,
  RECURRENCE_CYCLE_MAP,
  RECURRING_STATUS_MAP,
  type InvoiceStatus,
  type RecurrenceCycle,
  type RecurringStatus,
} from '@goyone/shared';
import { api } from '../../lib/api';
import { date, money } from '../../lib/format';
import { useAuth } from '../../auth/AuthProvider';
import { Badge, EmptyState, ErrorState, PageHead, Stat } from '../../components/ui';

interface Run {
  id: string;
  periodStart: string;
  periodEnd: string;
  error: string | null;
  invoice: {
    id: string;
    number: string;
    status: InvoiceStatus;
    total: string;
    paidAmount: string;
  } | null;
}

interface Detail {
  id: string;
  title: string;
  status: RecurringStatus;
  cycle: RecurrenceCycle;
  startDate: string;
  endDate: string | null;
  nextRunAt: string;
  lastRunAt: string | null;
  dayOfMonth: number;
  dueDays: number;
  autoIssue: boolean;
  periodTotal: number;
  taxRate: string;
  notes: string | null;
  terms: string | null;
  account: { id: string; name: string; email: string | null };
  contract: { id: string; number: string; title: string } | null;
  lines: Array<{
    id: string;
    description: string;
    quantity: string;
    unit: string;
    unitPrice: string;
    discount: string;
  }>;
  runs: Run[];
}

export function RecurringDetail() {
  const { id = '' } = useParams();
  const { atLeast } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const canManage = atLeast('MANAGER');

  const detail = useQuery({
    queryKey: ['recurring', id],
    queryFn: () => api<Detail>(`/finance/recurring/${id}`),
  });

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['recurring'] });

  const run = useMutation({
    mutationFn: () =>
      api<{ issued: number; skipped: number }>(`/finance/recurring/${id}/run`, {
        method: 'POST',
      }),
    onSuccess: refresh,
  });

  const setStatus = useMutation({
    mutationFn: (status: RecurringStatus) =>
      api(`/finance/recurring/${id}/status`, { method: 'PATCH', body: { status } }),
    onSuccess: refresh,
  });

  const remove = useMutation({
    mutationFn: () => api(`/finance/recurring/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      refresh();
      navigate('/finance/recurring');
    },
  });

  if (detail.isError) {
    return <ErrorState error={detail.error} onRetry={() => detail.refetch()} />;
  }

  const r = detail.data;
  const today = new Date().toISOString().slice(0, 10);
  const isDue = r?.status === 'ACTIVE' && r.nextRunAt <= today;

  return (
    <div className="page">
      <PageHead
        title={r?.title ?? 'اشتراك دوري'}
        subtitle={r ? `${r.account.name} — ${RECURRENCE_CYCLE_MAP[r.cycle].label}` : undefined}
        actions={
          r &&
          canManage && (
            <>
              <Link to="/finance/recurring" className="btn">
                رجوع
              </Link>
              <Link to={`/finance/recurring/${id}/edit`} className="btn">
                تعديل
              </Link>

              {r.status === 'ACTIVE' && (
                <>
                  <button className="btn" onClick={() => setStatus.mutate('PAUSED')}>
                    إيقاف مؤقت
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => run.mutate()}
                    disabled={run.isPending}
                  >
                    {run.isPending ? 'جارٍ الإصدار' : 'إصدار الآن'}
                  </button>
                </>
              )}

              {r.status === 'PAUSED' && (
                <button className="btn btn-primary" onClick={() => setStatus.mutate('ACTIVE')}>
                  استئناف
                </button>
              )}

              {r.status !== 'ENDED' && (
                <button className="btn btn-danger" onClick={() => remove.mutate()}>
                  إنهاء
                </button>
              )}
            </>
          )
        }
      />

      {run.isSuccess && (
        <div className="alert alert-info mb-4">
          {run.data.issued > 0
            ? `أُصدرت ${run.data.issued} فاتورة.`
            : 'لا دورات مستحقة الآن — كل ما فات صدر بالفعل.'}
        </div>
      )}

      {(run.isError || setStatus.isError || remove.isError) && (
        <div className="alert alert-danger mb-4">
          {[run.error, setStatus.error, remove.error].find(Boolean) instanceof Error
            ? ([run.error, setStatus.error, remove.error].find(Boolean) as Error).message
            : 'تعذّرت العملية.'}
        </div>
      )}

      {r && (
        <>
          <div className="stats">
            <Stat
              label="الحالة"
              value={
                <Badge kind={RECURRING_STATUS_MAP[r.status].kind}>
                  {RECURRING_STATUS_MAP[r.status].label}
                </Badge>
              }
            />
            <Stat label="قيمة الدورة" value={<span className="num">{money(r.periodTotal)}</span>} />
            <Stat
              label="الإصدار التالي"
              value={
                r.status === 'ENDED' ? (
                  '—'
                ) : (
                  <span className={`num ${isDue ? 'neg' : ''}`}>{date(r.nextRunAt)}</span>
                )
              }
            />
            <Stat label="فواتير صدرت" value={r.runs.filter((x) => x.invoice).length} />
          </div>

          {r.status === 'PAUSED' && (
            <div className="alert alert-warning mb-4">
              الاشتراك موقوف مؤقتًا فلا تُصدر منه فواتير. عند الاستئناف يبدأ من الدورة القادمة
              ولا يُصدر ما فات أثناء التوقف.
            </div>
          )}

          <div className="builder">
            <div className="card">
              <div className="card-head">
                <h2 className="card-title">البنود</h2>
              </div>
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>البند</th>
                      <th>الكمية</th>
                      <th>السعر</th>
                      <th>الإجمالي</th>
                    </tr>
                  </thead>
                  <tbody>
                    {r.lines.map((l) => (
                      <tr key={l.id}>
                        <td className="cell-strong">{l.description}</td>
                        <td className="num cell-mute">
                          {Number(l.quantity)} {l.unit}
                        </td>
                        <td className="num">{money(l.unitPrice)}</td>
                        <td className="num">
                          {money(
                            Number(l.quantity) * Number(l.unitPrice) - Number(l.discount),
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="card">
              <div className="card-head">
                <h2 className="card-title">الجدولة</h2>
              </div>
              <div className="card-body">
                <div className="totals-box">
                  <div className="totals-row">
                    <span className="mute">الدورة</span>
                    <span>{RECURRENCE_CYCLE_MAP[r.cycle].label}</span>
                  </div>
                  <div className="totals-row">
                    <span className="mute">يوم الإصدار</span>
                    <span className="num">{r.dayOfMonth}</span>
                  </div>
                  <div className="totals-row">
                    <span className="mute">من</span>
                    <span className="num">{date(r.startDate)}</span>
                  </div>
                  <div className="totals-row">
                    <span className="mute">إلى</span>
                    <span className="num">{r.endDate ? date(r.endDate) : 'مفتوح'}</span>
                  </div>
                  <div className="totals-row">
                    <span className="mute">مهلة السداد</span>
                    <span className="num">{r.dueDays} يومًا</span>
                  </div>
                  <div className="totals-row totals-final">
                    <span>وضع الإصدار</span>
                    <span>{r.autoIssue ? 'إرسال تلقائي' : 'مسودة للمراجعة'}</span>
                  </div>
                </div>

                {r.contract && (
                  <p className="tiny mute mt-3">
                    مرتبط بالعقد{' '}
                    <Link to={`/sales/contracts`} className="link num">
                      {r.contract.number}
                    </Link>
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="card mt-4">
            <div className="card-head">
              <h2 className="card-title">سجل الإصدار</h2>
            </div>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>الدورة</th>
                    <th>الفاتورة</th>
                    <th>القيمة</th>
                    <th>المحصَّل</th>
                    <th>الحالة</th>
                  </tr>
                </thead>
                <tbody>
                  {r.runs.map((x) => (
                    <tr key={x.id}>
                      <td className="num cell-mute">
                        {date(x.periodStart)} — {date(x.periodEnd)}
                      </td>
                      <td>
                        {x.invoice ? (
                          <Link
                            to={`/finance/invoices/${x.invoice.id}`}
                            className="cell-strong link num"
                          >
                            {x.invoice.number}
                          </Link>
                        ) : (
                          <span className="cell-mute">{x.error ?? 'لم تُنشأ'}</span>
                        )}
                      </td>
                      <td className="num">{x.invoice ? money(x.invoice.total) : '—'}</td>
                      <td className="num cell-mute">
                        {x.invoice ? money(x.invoice.paidAmount) : '—'}
                      </td>
                      <td>
                        {x.invoice && (
                          <Badge kind={INVOICE_STATUS_MAP[x.invoice.status].kind}>
                            {INVOICE_STATUS_MAP[x.invoice.status].label}
                          </Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {r.runs.length === 0 && (
              <EmptyState
                title="لم تُصدر دورات بعد"
                text="أول فاتورة تصدر في موعدها، أو بضغط إصدار الآن."
              />
            )}
          </div>
        </>
      )}
    </div>
  );
}
